import { useId, useRef, type ReactNode } from 'react'
import { AgencyMark, BANNER, Flag, Icon, Identifier, type Mission } from '@/lib'
import type { TemplateLink } from './types'

/**
 * The frame every template shares: skip link, official banner, agency header,
 * main content, footer and agency identifier. It reads nothing from the site's
 * preferences, so the same markup renders on the server for the HTML export.
 */
export function TemplatePage({
  mission,
  link,
  current,
  credit,
  children,
}: {
  mission: Mission
  link: TemplateLink
  current?: string
  credit?: ReactNode
  children: ReactNode
}) {
  const nav = [
    { label: 'Services', href: link('service-landing') },
    { label: 'News', href: '#news' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ]
  return (
    <div className="mds-page" data-template-root="" data-nav="links">
      <a className="skip-link" href="#content">
        Skip to main content
      </a>
      <TemplateBanner />
      <header className="mds-header mds-page__header">
        <div className="mds-container mds-header__inner">
          <a className="mds-header__home" href={link('agency-home')} aria-label={`${mission.name} home`}>
            <span className="mds-lockup">
              <AgencyMark mission={mission} variant="seal" className="mds-lockup__mark" />
              <span className="mds-lockup__text">
                <span className="mds-lockup__word">{mission.name}</span>
              </span>
            </span>
          </a>
          <div className="mds-header__nav-wrap">
            <nav className="mds-header__nav" aria-label="Main">
              {nav.map((item) => (
                <a
                  key={item.label}
                  className={`mds-header__link${item.label === current ? ' active' : ''}`}
                  href={item.href}
                  aria-current={item.label === current ? 'page' : undefined}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
          <details className="mds-page__menu">
            <summary className="mds-button mds-button--primary mds-button--sm">Menu</summary>
            <nav aria-label="Main" className="mds-page__menu-list">
              {nav.map((item) => (
                <a key={item.label} href={item.href} aria-current={item.label === current ? 'page' : undefined}>
                  {item.label}
                </a>
              ))}
            </nav>
          </details>
        </div>
      </header>
      <main id="content" tabIndex={-1} className="mds-page__main">
        {children}
      </main>
      <footer className="mds-site-footer">
        <div className="mds-footer mds-footer--editorial">
          <div className="mds-container mds-footer__editorial">
            <a href={link('agency-home')}>{mission.name}</a>
            <nav className="mds-footer__links" aria-label="Footer">
              <a href={link('service-landing')}>Services</a>
              <a href="#contact">Contact</a>
              <a href={link('search-results')}>Search</a>
              <a href="#about">About</a>
            </nav>
            {credit && <p className="mds-hint">{credit}</p>}
          </div>
        </div>
      </footer>
      <Identifier mission={mission} />
    </div>
  )
}

/**
 * The official banner, centered, as MDS shows it by default: the line opens a
 * dialog that explains how to tell an official website.
 */
function TemplateBanner() {
  const dialog = useRef<HTMLDialogElement>(null)
  const dialogId = useId()
  const titleId = useId()
  return (
    <section className="mds-banner mds-banner--center mds-banner--dialog" aria-label="Official website of the United States government">
      <button
        type="button"
        className="mds-banner__button"
        aria-haspopup="dialog"
        aria-controls={dialogId}
        onClick={() => dialog.current?.showModal()}
      >
        <Flag variant="flag" className="mds-banner__flag" />
        <span>{BANNER.official}</span>
        <Icon name="info" size={16} className="mds-banner__info" />
      </button>
      <dialog
        ref={dialog}
        id={dialogId}
        className="mds-dialog mds-dialog--notice"
        aria-labelledby={titleId}
        onClick={(event) => {
          if (event.target === dialog.current) dialog.current.close()
        }}
      >
        <div className="mds-dialog__inner">
          <div className="mds-dialog__head">
            <h2 id={titleId} className="mds-h3">
              U.S. government websites
            </h2>
            <button type="button" className="mds-icon-button" aria-label="Close" data-dialog-close="" onClick={() => dialog.current?.close()}>
              <Icon name="close" />
            </button>
          </div>
          <div className="mds-banner-notice">
            <div className="mds-banner-notice__item">
              <span className="mds-banner-notice__icon" aria-hidden="true">
                <Icon name="landmark" />
              </span>
              <div>
                <h3>Official websites use .gov</h3>
                <p>
                  A <strong>.gov</strong> website belongs to an official government organization in the United States.
                </p>
              </div>
            </div>
            <div className="mds-banner-notice__item">
              <span className="mds-banner-notice__icon" aria-hidden="true">
                <Icon name="lock" />
              </span>
              <div>
                <h3>Secure .gov websites use HTTPS</h3>
                <p>
                  A <strong>lock</strong> or <strong>https://</strong> means you have safely connected to the .gov website.
                  Share sensitive information only on official, secure websites.
                </p>
              </div>
            </div>
          </div>
        </div>
      </dialog>
    </section>
  )
}

/** A search form, as the header, the 404 page and the results page use it. */
export function TemplateSearch({ id, action, value }: { id: string; action: string; value?: string }) {
  return (
    <form className="mds-search" role="search" action={action}>
      <label className="mds-visually-hidden" htmlFor={id}>
        Search
      </label>
      <input id={id} name="q" className="mds-search__input" type="search" placeholder="Search" defaultValue={value} />
      <button type="submit" className="mds-button mds-button--primary mds-button--sm">
        Search
      </button>
    </form>
  )
}
